/**
 * EmployeeFormPage
 * Page for creating and editing employees
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Save,
  NavigateNext,
  NavigateBefore,
  Delete,
  Add,
  AccessibleForward,
  HealthAndSafety,
  AccountBalance
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useEmployee, useCEPLookup, useCompanySelection } from '../../hooks';
import { PageShell } from '../../components';
import { CompanySelectionModal, CompanyInfoBox } from '../../components/forms';
import { companyService, departmentService, occupationService, employeeService } from '../../services';
import { CidAutocomplete } from '../medical-leaves/components/CidAutocomplete';
import { AposentadoriaEspecialFields } from './components/AposentadoriaEspecialFields';
import {
  avisoDeDivergencia,
  chaveDoAviso,
  decidirPreenchimento,
  type CampoAposentadoria,
  type EventoDoCargo,
  type OrigensDosCampos,
} from './aposentadoriaEspecial';
import { esocialParaEnvio } from './esocialParaEnvio';
import { useAposentadoriaEspecialDoCargo } from '../../hooks/useAposentadoriaEspecialDoCargo';
import {
  EmployeeFormData,
  EmployeeGender,
  MaritalStatus,
  GENDER_LABELS,
  MARITAL_STATUS_LABELS,
  EmployeeStatus,
  EMPLOYEE_STATUS_LABELS,
  ENQUADRAMENTO_POR_CODIGO,
  type CodigoAposentadoriaEspecial,
  WorkLocation,
  WORK_LOCATION_LABELS,
} from '../../types/employee';
import { unitService } from '../../services/unitService';
import { BRAZILIAN_STATES } from '../../types/common';
import {
  validateEmployeeForm,
  validateSituacaoComDemissao,
  validateCPF,
  validateBirthDate,
  validatePIS,
  validateCTPS,
  validateEmail,
  validatePhone,
  validateCEP,
  validateAdmissionDate
} from '../../utils/employeeValidations';
import { fetchAddressByCEP } from '../../utils/cepUtils';
import {
  formatCPF,
  formatPhone,
  formatCEP,
  formatPIS,
  formatCTPS,
  unformatCPF,
  unformatPhone,
  unformatCEP
} from '../../utils/employeeFormatters';

const steps = [
  'Dados Pessoais',
  'Documentação',
  'Contato',
  'Dados Ocupacionais',
  'PCD e CIPA'
];

// Campos obrigatórios por etapa
const REQUIRED_FIELDS_BY_STEP: Record<number, string[]> = {
  0: ['name', 'cpf', 'birthDate', 'motherName', 'gender'], // Dados Pessoais
  1: [], // Documentação (CNH é opcional)
  2: ['address', 'addressNumber', 'neighborhood', 'city', 'state', 'zipCode'], // Contato
  3: ['companyId', 'departmentId', 'occupationId', 'admissionDate'], // Dados Ocupacionais
  4: [] // PCD e CIPA (tudo opcional)
};

/** Letra de 16px nos botões da barra de navegação (o `large` do MUI dá 15px). */
const BOTAO_DE_NAVEGACAO = { fontSize: 16 } as const;

export const EmployeeFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Estados para as opções de select
  const [_companies, setCompanies] = useState<Array<{ id: number; tradeName: string }>>([]);
  const [allDepartments, setAllDepartments] = useState<Array<{ id: number; name: string; companyId: number }>>([]);
  const [allOccupations, setAllOccupations] = useState<Array<{ id: number; name: string; departmentId: number }>>([]);
  const [allUnits, setAllUnits] = useState<Array<{ id: number; name: string; companyId: number }>>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Hooks
  const { employee, loading, saving, createEmployee, updateEmployee } = useEmployee(
    id ? parseInt(id) : undefined
  );
  const { lookupCEP, addressData } = useCEPLookup();
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode, currentCompanyId: String(employee?.companyId ?? '') });

  // Flag to prevent cascade resets during initial employee data load
  const isInitialLoadRef = React.useRef(false);

  // CPF validation states
  const [cpfValidating, setCpfValidating] = useState(false);
  const [cpfError, setCpfError] = useState<string | null>(null);

  // React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors }
  } = useForm<EmployeeFormData>({
    defaultValues: {
      name: '',
      socialName: '',
      cpf: '',
      birthDate: '',
      motherName: '',
      gender: '',
      maritalStatus: '',
      birthplace: '',
      cnh: '',
      cnhExpiration: '',
      ctps: '',
      serieCtps: '',
      ufCtps: '',
      pis: '',
      whatsapp: '',
      email: '',
      zipCode: '',
      address: '',
      addressNumber: '',
      neighborhood: '',
      city: '',
      state: '',
      emergencyContacts: [],
      companyId: '',
      departmentId: '',
      occupationId: '',
      employeeCode: '',
      admissionDate: '',
      dismissalDate: '',
      status: EmployeeStatus.ATIVO,
      leaveDate: '',
      workLocation: '',
      unitId: '',
      hasDisability: '',
      cidCode: '',
      disabilityDescription: '',
      socialSecurityClassification: '',
      cipa: {
        isMember: false,
        startDate: '',
        endDate: '',
        role: '',
        term: ''
      },
      esocial: {
        inicioCondEsocial: '',
        fimCondEsocial: '',
        motivo: ''
      }
    }
  });

  // Watch para filtros em cascata
  const selectedCompanyId = watch('companyId');
  const selectedDepartmentId = watch('departmentId');
  const zipCode = watch('zipCode');

  // Filtrar setores pela empresa selecionada
  const filteredDepartments = selectedCompanyId 
    ? allDepartments.filter(d => String(d.companyId) === String(selectedCompanyId))
    : allDepartments;

  // Filtrar cargos pelo setor selecionado
  const filteredOccupations = selectedDepartmentId
    ? allOccupations.filter(o => String(o.departmentId) === String(selectedDepartmentId))
    : allOccupations;

  // Filtrar unidades pela empresa selecionada (posto de trabalho)
  const filteredUnits = selectedCompanyId
    ? allUnits.filter(u => String(u.companyId) === String(selectedCompanyId))
    : [];
  const watchWorkLocation = watch('workLocation');

  // Código GFIP e Enquadramento: o servidor calcula pelos riscos do cargo (#302). A origem de
  // cada campo fica numa referência, fora do estado do formulário: ela decide se o cálculo pode
  // escrever, e não faz parte do que se grava.
  const selectedOccupationId = watch('occupationId');
  const gfipAtual = watch('esocial.gfip') ?? '';
  const enquadramentoAtual = watch('socialSecurityClassification');
  const origensAposentadoriaRef = React.useRef<OrigensDosCampos>({});
  const aposentadoriaCarregadaDoRef = React.useRef<string | null>(null);
  const [mantidoPara, setMantidoPara] = useState<string | null>(null);
  const { estado: calculoDoCargo, tentarDeNovo: recalcularCargo } =
    useAposentadoriaEspecialDoCargo(selectedOccupationId);
  const respostaDoCargo = calculoDoCargo.tipo === 'resposta' ? calculoDoCargo.resposta : null;
  const aviso = avisoDeDivergencia(
    respostaDoCargo?.codigo ?? null,
    gfipAtual,
    enquadramentoAtual,
    mantidoPara
  );

  const aplicarPreenchimento = useCallback(
    (evento: EventoDoCargo) => {
      const atual = {
        valores: {
          gfip: getValues('esocial.gfip') ?? '',
          enquadramento: getValues('socialSecurityClassification'),
        },
        origens: origensAposentadoriaRef.current,
      };
      const proximo = decidirPreenchimento(atual, evento);
      origensAposentadoriaRef.current = proximo.origens;
      if (proximo.valores.gfip !== atual.valores.gfip) {
        setValue('esocial.gfip', proximo.valores.gfip);
      }
      if (proximo.valores.enquadramento !== atual.valores.enquadramento) {
        setValue('socialSecurityClassification', proximo.valores.enquadramento);
      }
    },
    [getValues, setValue]
  );

  // Cada resposta do servidor passa pela mesma decisão; a do cargo anterior já foi descartada
  // pelo gancho, então aqui só chega a do cargo atual.
  useEffect(() => {
    if (respostaDoCargo) aplicarPreenchimento({ tipo: 'resposta', resposta: respostaDoCargo });
  }, [respostaDoCargo, aplicarPreenchimento]);

  // Sem cargo, ou cargo ainda sem resposta (calculando, 403, falha): o que o sistema pôs vinha de
  // outro cargo e não vale.
  useEffect(() => {
    if (calculoDoCargo.tipo === 'sem-cargo') aplicarPreenchimento({ tipo: 'cargo-limpo' });
    else if (calculoDoCargo.tipo !== 'resposta') aplicarPreenchimento({ tipo: 'sem-resposta' });
  }, [calculoDoCargo.tipo, aplicarPreenchimento]);

  // Indo da edição para o cadastro novo o componente é o mesmo: as origens `salvo` do funcionário
  // anterior não podem sobrar, senão o cálculo nunca preenche.
  useEffect(() => {
    if (isEditMode) return;
    // Vindo de uma edição, o formulário não é reiniciado: devolve ao valor em branco o que é do
    // funcionário anterior nesta seção. Cadastro aberto direto não passa por aqui.
    if (aposentadoriaCarregadaDoRef.current !== null) {
      setValue('esocial.inicioCondEsocial', '');
      setValue('esocial.fimCondEsocial', '');
      setValue('esocial.motivo', '');
      setValue('esocial.estabilidade', '');
      setValue('esocial.gfip', '');
      setValue('socialSecurityClassification', '');
    }
    aposentadoriaCarregadaDoRef.current = null;
    origensAposentadoriaRef.current = {};
    setMantidoPara(null);
  }, [isEditMode, setValue]);

  const registrarEdicaoDaPessoa = (campo: CampoAposentadoria) => {
    origensAposentadoriaRef.current = { ...origensAposentadoriaRef.current, [campo]: 'pessoa' };
  };

  const usarCodigoCalculado = (codigo: CodigoAposentadoriaEspecial) => {
    origensAposentadoriaRef.current = { gfip: 'sistema', enquadramento: 'sistema' };
    setValue('esocial.gfip', String(codigo));
    setValue('socialSecurityClassification', ENQUADRAMENTO_POR_CODIGO[codigo]);
    setMantidoPara(null);
  };

  // Emergency Contacts Field Array
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'emergencyContacts'
  });

  // Watch CEP for auto-fill
  useEffect(() => {
    if (zipCode && zipCode.replace(/\D/g, '').length === 8) {
      lookupCEP(zipCode);
    }
  }, [zipCode, lookupCEP]);

  useEffect(() => {
    if (addressData) {
      setValue('address', addressData.address);
      setValue('neighborhood', addressData.neighborhood);
      setValue('city', addressData.city);
      setValue('state', addressData.state);
    }
  }, [addressData, setValue]);

  // Load employee data in edit mode
  useEffect(() => {
    if (employee && isEditMode) {
      // Prevent cascade reset effects from clearing occupation/department
      isInitialLoadRef.current = true;

      setValue('name', employee.name);
      setValue('socialName', employee.socialName ?? '');
      setValue('cpf', employee.cpf);
      setValue('birthDate', employee.birthDate?.split('T')[0] || '');
      setValue('motherName', employee.motherName ?? '');
      setValue('gender', employee.gender);
      setValue('maritalStatus', employee.maritalStatus ?? '');
      setValue('birthplace', employee.birthplace ?? '');
      setValue('cnh', employee.cnh ?? '');
      setValue('cnhExpiration', employee.cnhExpiration?.split('T')[0] || '');
      setValue('whatsapp', employee.whatsapp ?? '');
      setValue('email', employee.email ?? '');
      setValue('zipCode', employee.zipCode ?? '');
      setValue('address', employee.address ?? '');
      setValue('addressNumber', employee.addressNumber ?? '');
      setValue('neighborhood', employee.neighborhood ?? '');
      setValue('city', employee.city ?? '');
      setValue('state', employee.state ?? '');
      setValue('companyId', employee.companyId);
      setValue('departmentId', employee.departmentId);
      setValue('occupationId', employee.occupationId);
      setValue('employeeCode', employee.employeeCode ?? '');
      setValue('admissionDate', employee.admissionDate?.split('T')[0] || '');
      setValue('dismissalDate', employee.dismissalDate?.split('T')[0] || '');
      setValue('leaveDate', employee.leaveDate?.split('T')[0] || '');
      setValue('workLocation', employee.workLocation ?? '');
      setValue('unitId', employee.unitId ?? '');
      setValue('hasDisability', employee.hasDisability ?? '');
      setValue('cidCode', employee.cidCode ?? '');
      setValue('disabilityDescription', employee.disabilityDescription ?? '');
      setValue('status', employee.status ?? 'ATIVO');

      // Carregar dados de documentos (CTPS, PIS)
      if (employee.documents) {
        setValue('ctps', employee.documents.ctps ?? '');
        setValue('serieCtps', employee.documents.serieCtps ?? '');
        setValue('ufCtps', employee.documents.ufCtps ?? '');
        setValue('pis', employee.documents.pis ?? '');
      }

      // Carregar contatos de emergência
      if (employee.emergencyContacts && employee.emergencyContacts.length > 0) {
        setValue('emergencyContacts', employee.emergencyContacts);
      }

      // Carregar dados CIPA
      if (employee.cipa) {
        setValue('cipa', {
          isMember: employee.cipa.isMember,
          startDate: employee.cipa.startDate?.split('T')[0] || '',
          endDate: employee.cipa.endDate?.split('T')[0] || '',
          role: employee.cipa.role ?? '',
          term: employee.cipa.term ?? ''
        });
      }

      // Carregar dados eSocial. GFIP e Enquadramento entram uma vez só por funcionário: se
      // `employee` mudar de referência (refresh), o que o cálculo ou a pessoa já pôs não é apagado
      // nem as origens reiniciadas. Outro funcionário (mudou o :id) recarrega tudo e zera as
      // origens. O que veio salvo nunca é sobrescrito pelo cálculo, mesmo igual a ele.
      // Sem eSocial salvo, os campos voltam ao valor em branco só quando o funcionário muda: numa
      // releitura do mesmo funcionário não se apaga o que a pessoa já digitou.
      const primeiraCargaDesteFuncionario = aposentadoriaCarregadaDoRef.current !== String(employee.id);
      if (employee.esocial) {
        setValue('esocial.inicioCondEsocial', employee.esocial.inicioCondEsocial?.split('T')[0] || '');
        setValue('esocial.fimCondEsocial', employee.esocial.fimCondEsocial?.split('T')[0] || '');
        setValue('esocial.motivo', employee.esocial.motivo ?? '');
        setValue('esocial.estabilidade', employee.esocial.estabilidade?.split('T')[0] || '');
      } else if (primeiraCargaDesteFuncionario) {
        setValue('esocial.inicioCondEsocial', '');
        setValue('esocial.fimCondEsocial', '');
        setValue('esocial.motivo', '');
        setValue('esocial.estabilidade', '');
      }
      if (primeiraCargaDesteFuncionario) {
        aposentadoriaCarregadaDoRef.current = String(employee.id);
        setValue('esocial.gfip', employee.esocial?.gfip ?? '');
        setValue('socialSecurityClassification', employee.socialSecurityClassification ?? '');
        setMantidoPara(null);
        origensAposentadoriaRef.current = {
          ...(employee.esocial?.gfip?.trim() ? { gfip: 'salvo' as const } : {}),
          ...(employee.socialSecurityClassification ? { enquadramento: 'salvo' as const } : {}),
        };
      }

      // Allow cascade resets again after React processes all setValue calls
      setTimeout(() => { isInitialLoadRef.current = false; }, 0);
    }
  }, [employee, isEditMode, setValue]);

  // Carregar opções para os selects
  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const [companiesResponse, departmentsResponse, occupationsResponse, unitsResponse] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 }),
          occupationService.getOccupations({ page: 1, limit: 1000 }),
          unitService.getUnits({ limit: 1000 })
        ]);

        const companiesData = companiesResponse.data.map((c: any) => ({ 
          id: c.id, 
          tradeName: c.tradeName 
        }));
        
        const departmentsData = departmentsResponse.data.map((d: any) => ({ 
          id: d.id, 
          name: d.name,
          companyId: d.companyId
        }));
        
        const occupationsData = occupationsResponse.data.map((o: any) => ({ 
          id: o.id, 
          name: o.name,
          departmentId: o.departmentId
        }));

        console.log('📊 Dados carregados:', {
          companies: companiesData.length,
          departments: departmentsData.length,
          occupations: occupationsData.length
        });

        const unitsData = unitsResponse.data.map((u: { id: number; name: string; companyId: number }) => ({
          id: u.id,
          name: u.name,
          companyId: u.companyId
        }));

        setCompanies(companiesData);
        setAllDepartments(departmentsData);
        setAllOccupations(occupationsData);
        setAllUnits(unitsData);
      } catch (error) {
        console.error('Erro ao carregar opções:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, []);

  // Resetar setor quando empresa mudar
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (selectedCompanyId && !loadingOptions) {
      const currentDepartment = getValues('departmentId');
      // Se o setor atual não pertence à empresa selecionada, limpar
      if (currentDepartment) {
        const dept = allDepartments.find(d => d.id === Number(currentDepartment));
        if (dept && dept.companyId !== Number(selectedCompanyId)) {
          setValue('departmentId', '');
          setValue('occupationId', '');
        }
      }
      // Se a unidade (posto) atual não pertence à empresa selecionada, limpar
      const currentUnit = getValues('unitId');
      if (currentUnit) {
        const unit = allUnits.find(u => String(u.id) === String(currentUnit));
        if (unit && String(unit.companyId) !== String(selectedCompanyId)) {
          setValue('unitId', '');
        }
      }
    }
  }, [selectedCompanyId, allDepartments, allUnits, loadingOptions, setValue, getValues]);

  // Limpar unidade (posto) quando "Tipo de Empresa" deixar de ser UNIDADE
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (watchWorkLocation !== WorkLocation.UNIDADE && getValues('unitId') !== '') {
      setValue('unitId', '');
    }
  }, [watchWorkLocation, setValue, getValues]);

  // Resetar cargo quando setor mudar
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (selectedDepartmentId && !loadingOptions) {
      const currentOccupation = getValues('occupationId');
      // Se o cargo atual não pertence ao setor selecionado, limpar
      if (currentOccupation) {
        const occ = allOccupations.find(o => o.id === Number(currentOccupation));
        if (occ && occ.departmentId !== Number(selectedDepartmentId)) {
          setValue('occupationId', '');
        }
      }
    }
  }, [selectedDepartmentId, allOccupations, loadingOptions, setValue, getValues]);

  // Sync company selection (from modal or auto-fill) to form
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = getValues('companyId');
      if (String(currentValue) !== selectedCompany.id) {
        setValue('companyId', Number(selectedCompany.id) || '');
      }
    }
  }, [selectedCompany, getValues, setValue]);

  // CPF validation handler (onBlur)
  const handleCPFValidation = async (cpfValue: string) => {
    setCpfError(null);
    const cleaned = cpfValue.replace(/\D/g, '');
    if (cleaned.length !== 11) return;

    if (!validateCPF(cpfValue)) {
      setCpfError('CPF inválido');
      return;
    }

    // Skip uniqueness check if CPF hasn't changed in edit mode
    if (isEditMode && employee && cleaned === employee.cpf?.replace(/\D/g, '')) {
      return;
    }

    setCpfValidating(true);
    try {
      const currentCompanyId = getValues('companyId') ? Number(getValues('companyId')) : undefined;
      const result = await employeeService.validateCPF(cleaned, isEditMode && id ? parseInt(id) : undefined, currentCompanyId);
      if (!result.valid) {
        setCpfError(result.message || 'CPF já cadastrado no sistema');
      }
    } catch {
      // Silently ignore network errors — validation will happen on submit
    } finally {
      setCpfValidating(false);
    }
  };

  // Validar campos obrigatórios antes de avançar
  const validateStep = (): boolean => {
    const requiredFields = REQUIRED_FIELDS_BY_STEP[activeStep] ?? [];
    const formValues = watch();
    const errors: Record<string, string> = {};

    console.log('🔍 Validando Step:', activeStep);
    console.log('📋 Campos obrigatórios:', requiredFields);
    console.log('📝 Valores do formulário:', formValues);

    for (const field of requiredFields) {
      const value = formValues[field as keyof EmployeeFormData];
      console.log(`  - ${field}:`, value, '→', (!value || (typeof value === 'string' && value.trim() === '')) ? '❌ INVÁLIDO' : '✅ VÁLIDO');
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field] = 'Campo obrigatório';
      }
    }

    // Validações por step
    if (activeStep === 0) {
      // CPF
      if (formValues.cpf) {
        const cpfUnchanged = isEditMode && employee && formValues.cpf.replace(/\D/g, '') === employee.cpf?.replace(/\D/g, '');
        if (!cpfUnchanged && !validateCPF(formValues.cpf)) {
          errors.cpf = 'CPF inválido';
        }
        if (cpfError) {
          errors.cpf = cpfError;
        }
      }
      // Data de nascimento
      if (formValues.birthDate) {
        const birthResult = validateBirthDate(formValues.birthDate);
        if (!birthResult.valid) {
          errors.birthDate = birthResult.message!;
        }
      }
    }

    if (activeStep === 1) {
      // PIS (opcional, mas se preenchido deve ser válido)
      if (formValues.pis && !validatePIS(formValues.pis)) {
        errors.pis = 'PIS/PASEP inválido';
      }
      // CTPS (opcional, mas se preenchido deve ser válido)
      if (formValues.ctps && !validateCTPS(formValues.ctps)) {
        errors.ctps = 'CTPS inválida (deve ter 7 dígitos)';
      }
    }

    if (activeStep === 2) {
      // Email (opcional, mas se preenchido deve ser válido)
      if (formValues.email && !validateEmail(formValues.email)) {
        errors.email = 'E-mail inválido';
      }
      // WhatsApp (opcional, mas se preenchido deve ser válido)
      if (formValues.whatsapp && !validatePhone(formValues.whatsapp)) {
        errors.whatsapp = 'WhatsApp inválido (deve ter 10 ou 11 dígitos)';
      }
      // CEP
      if (formValues.zipCode && !validateCEP(formValues.zipCode)) {
        errors.zipCode = 'CEP inválido (deve ter 8 dígitos)';
      }
    }

    if (activeStep === 3) {
      // Data de admissão
      if (formValues.admissionDate) {
        const admissionResult = validateAdmissionDate(formValues.admissionDate, formValues.birthDate);
        if (!admissionResult.valid) {
          errors.admissionDate = admissionResult.message!;
        }
      }
    }

    if (activeStep === 3) {
      Object.assign(errors, validateSituacaoComDemissao(formValues.status, formValues.dismissalDate));
    }

    if (activeStep === 4) {
      // CIPA
      if (formValues.cipa?.isMember) {
        if (!formValues.cipa.startDate || formValues.cipa.startDate.trim() === '') {
          errors['cipa.startDate'] = 'Data de início é obrigatória para membros CIPA';
        }
        if (!formValues.cipa.role || formValues.cipa.role.trim() === '') {
          errors['cipa.role'] = 'Cargo na CIPA é obrigatório para membros CIPA';
        }
      }
    }

    console.log('❌ Erros encontrados:', errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
      setFormErrors({});
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    setActiveStep((prev) => prev - 1);
  };

  const onSubmit = async (data: EmployeeFormData) => {
    console.log('💾 Iniciando submit do formulário');
    console.log('📝 Dados recebidos:', data);
    
    // Validate
    const errors = validateEmployeeForm(data);

    // Skip CPF checksum validation in edit mode if CPF hasn't changed
    if (isEditMode && employee && errors.cpf === 'CPF inválido') {
      const cpfUnchanged = data.cpf.replace(/\D/g, '') === employee.cpf?.replace(/\D/g, '');
      if (cpfUnchanged) {
        delete errors.cpf;
      }
    }

    console.log('❌ Erros de validação:', errors);
    console.log('🔢 Total de erros:', Object.keys(errors).length);

    if (Object.keys(errors).length > 0) {
      console.log('🚫 Submit bloqueado devido a erros de validação');
      setFormErrors(errors);
      return;
    }

    console.log('✅ Validação passou, preparando dados...');

    // Clean data — convert empty optional strings to undefined
    const cleanedData = {
      ...data,
      cpf: unformatCPF(data.cpf),
      email: data.email || undefined,
      whatsapp: data.whatsapp ? unformatPhone(data.whatsapp) : undefined,

      zipCode: unformatCEP(data.zipCode),
      companyId: Number(data.companyId),
      departmentId: Number(data.departmentId),
      occupationId: Number(data.occupationId),
      gender: data.gender ?? undefined,
      maritalStatus: data.maritalStatus ?? undefined,
      workLocation: data.workLocation || undefined,
      unitId: data.unitId === '' ? undefined : data.unitId,
      esocial: esocialParaEnvio(data.esocial, isEditMode ? 'edicao' : 'criacao'),
    };

    console.log('🧹 Dados limpos:', cleanedData);

    if (isEditMode && id) {
      console.log('✏️ Atualizando funcionário ID:', id);
      await updateEmployee(parseInt(id), cleanedData as any);
    } else {
      console.log('➕ Criando novo funcionário');
      await createEmployee(cleanedData as any);
    }
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Funcionários', path: '/employees' },
    { label: isEditMode ? 'Editar' : 'Novo' },
  ];

  // Demitido só sai da situação pela reativação; o servidor recusa a troca pela edição.
  const demitidoGravado = isEditMode && employee?.status === EmployeeStatus.DEMITIDO;

  const pageTitle = isEditMode ? 'Editar Funcionário' : 'Novo Funcionário';
  const pageSubtitle = isEditMode
    ? 'Atualize os dados do funcionário'
    : 'Cadastre um novo funcionário no sistema';

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={pageTitle} subtitle={pageSubtitle}>
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={breadcrumbs} title={pageTitle} subtitle={pageSubtitle}>
      {/* Company Info Box — visible across all steps when manually selected */}
      {showInfoBox && selectedCompany && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper sx={{ p: 3, mb: 3 }}>
          {/* Step 0: Dados Pessoais */}
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Dados Pessoais
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Nome completo é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome Completo *"
                      fullWidth
                      error={!!errors.name || !!formErrors.name}
                      helperText={errors.name?.message || formErrors.name}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="cpf"
                  control={control}
                  rules={{ required: 'CPF é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CPF *"
                      fullWidth
                      placeholder="000.000.000-00"
                      inputProps={{ maxLength: 14 }}
                      value={formatCPF(field.value)}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '').slice(0, 11);
                        field.onChange(raw);
                        if (cpfError) setCpfError(null);
                      }}
                      onBlur={() => handleCPFValidation(field.value)}
                      error={!!errors.cpf || !!formErrors.cpf || !!cpfError}
                      helperText={errors.cpf?.message || formErrors.cpf || cpfError}
                      InputProps={{
                        endAdornment: cpfValidating ? (
                          <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                        ) : undefined
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Controller
                  name="socialName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome Social"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="birthDate"
                  control={control}
                  rules={{ required: 'Data de nascimento é obrigatória' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data de Nascimento *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.birthDate || !!formErrors.birthDate}
                      helperText={errors.birthDate?.message || formErrors.birthDate}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="motherName"
                  control={control}
                  rules={{ required: 'Nome da mãe é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Nome da Mãe *"
                      fullWidth
                      error={!!errors.motherName || !!formErrors.motherName}
                      helperText={errors.motherName?.message || formErrors.motherName}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="birthplace"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Naturalidade"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: 'Sexo é obrigatório' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.gender || !!formErrors.gender}>
                      <InputLabel>Sexo *</InputLabel>
                      <Select {...field} label="Sexo *">
                        <MenuItem value="">Selecione</MenuItem>
                        {Object.values(EmployeeGender).map((gender) => (
                          <MenuItem key={gender} value={gender}>
                            {GENDER_LABELS[gender]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="maritalStatus"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Estado Civil</InputLabel>
                      <Select {...field} label="Estado Civil">
                        <MenuItem value="">Selecione</MenuItem>
                        {Object.values(MaritalStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {MARITAL_STATUS_LABELS[status]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 1: Documentação */}
          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Documentação
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="cnh"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CNH"
                      fullWidth
                      placeholder="00000000000"
                      inputProps={{ maxLength: 11 }}
                      value={field.value?.replace(/\D/g, '').slice(0, 11) ?? ''}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="cnhExpiration"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Validade da CNH"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="pis"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="PIS/PASEP"
                      fullWidth
                      placeholder="000.00000.00-0"
                      value={formatPIS(field.value)}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      error={!!formErrors.pis}
                      helperText={formErrors.pis}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="ctps"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CTPS"
                      fullWidth
                      placeholder="0000000"
                      value={formatCTPS(field.value)}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 7))}
                      error={!!formErrors.ctps}
                      helperText={formErrors.ctps}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="serieCtps"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Série"
                      fullWidth
                      placeholder="0000"
                      value={(field.value || '').replace(/\D/g, '').slice(0, 4)}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="ufCtps"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>UF CTPS</InputLabel>
                      <Select {...field} label="UF CTPS">
                        <MenuItem value="">Selecione</MenuItem>
                        {BRAZILIAN_STATES.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 2: Contato */}
          {activeStep === 2 && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Informações de Contato
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="whatsapp"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="WhatsApp"
                      fullWidth
                      placeholder="(00) 00000-0000"
                      value={formatPhone(field.value)}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 11))}
                      error={!!formErrors.whatsapp}
                      helperText={formErrors.whatsapp}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="E-mail"
                      type="email"
                      fullWidth
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="zipCode"
                  control={control}
                  rules={{ required: 'CEP é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CEP *"
                      fullWidth
                      value={formatCEP(field.value)}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!errors.zipCode || !!formErrors.zipCode}
                      helperText={errors.zipCode?.message || formErrors.zipCode}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="address"
                  control={control}
                  rules={{ required: 'Endereço é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Endereço *"
                      fullWidth
                      error={!!errors.address || !!formErrors.address}
                      helperText={errors.address?.message || formErrors.address}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Controller
                  name="addressNumber"
                  control={control}
                  rules={{ required: 'Número é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Número *"
                      fullWidth
                      error={!!errors.addressNumber || !!formErrors.addressNumber}
                      helperText={errors.addressNumber?.message || formErrors.addressNumber}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Controller
                  name="neighborhood"
                  control={control}
                  rules={{ required: 'Bairro é obrigatório' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bairro *"
                      fullWidth
                      error={!!errors.neighborhood || !!formErrors.neighborhood}
                      helperText={errors.neighborhood?.message || formErrors.neighborhood}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="city"
                  control={control}
                  rules={{ required: 'Cidade é obrigatória' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cidade *"
                      fullWidth
                      error={!!errors.city || !!formErrors.city}
                      helperText={errors.city?.message || formErrors.city}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: 'UF é obrigatório' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.state || !!formErrors.state}>
                      <InputLabel>UF *</InputLabel>
                      <Select {...field} label="UF *">
                        <MenuItem value="">Selecione</MenuItem>
                        {BRAZILIAN_STATES.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                      {(errors.state || formErrors.state) && (
                        <Typography variant="caption" color="error">
                          {errors.state?.message || formErrors.state}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Emergency Contacts */}
              <Grid size={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Contatos de Emergência
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => append({
                      name: '',
                      relationship: '',
                      phone: '',
                      zipCode: '',
                      address: '',
                      addressNumber: '',
                      complement: '',
                      neighborhood: '',
                      city: '',
                      state: ''
                    })}
                  >
                    Adicionar
                  </Button>
                </Box>
              </Grid>

              {fields.map((field, index) => (
                <Grid key={field.id} size={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                              Contato {index + 1}
                            </Typography>
                            <IconButton color="error" size="small" onClick={() => remove(index)}>
                              <Delete />
                            </IconButton>
                          </Box>
                        </Grid>

                        {/* Nome, Parentesco, Telefone */}
                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.name`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Nome"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.relationship`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Parentesco"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.phone`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Telefone"
                                fullWidth
                                size="small"
                                placeholder="(00) 00000-0000"
                                value={formatPhone(field.value)}
                                onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, 11))}
                              />
                            )}
                          />
                        </Grid>

                        {/* Endereço Completo */}
                        <Grid size={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Endereço (opcional)
                          </Typography>
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 3
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.zipCode`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="CEP"
                                fullWidth
                                size="small"
                                value={formatCEP(field.value ?? '')}
                                onChange={(e) => {
                                  const cep = e.target.value.replace(/\D/g, '');
                                  field.onChange(cep);

                                  // Buscar endereço quando CEP estiver completo
                                  if (cep.length === 8) {
                                    // Usar fetchAddressByCEP diretamente para não interferir no estado compartilhado
                                    fetchAddressByCEP(cep).then(addressData => {
                                      if (addressData) {
                                        setValue(`emergencyContacts.${index}.address`, addressData.address ?? '');
                                        setValue(`emergencyContacts.${index}.neighborhood`, addressData.neighborhood ?? '');
                                        setValue(`emergencyContacts.${index}.city`, addressData.city ?? '');
                                        setValue(`emergencyContacts.${index}.state`, addressData.state ?? '');
                                      }
                                    });
                                  }
                                }}
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.address`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Endereço"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 3
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.addressNumber`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Número"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.complement`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Complemento"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 4
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.neighborhood`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Bairro"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 3
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.city`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Cidade"
                                fullWidth
                                size="small"
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          size={{
                            xs: 12,
                            md: 1
                          }}>
                          <Controller
                            name={`emergencyContacts.${index}.state`}
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth size="small">
                                <InputLabel>UF</InputLabel>
                                <Select {...field} label="UF">
                                  <MenuItem value="">-</MenuItem>
                                  {BRAZILIAN_STATES.map((state) => (
                                    <MenuItem key={state} value={state}>
                                      {state}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Step 3: Dados Ocupacionais */}
          {activeStep === 3 && (
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="h6" gutterBottom>
                  Dados Ocupacionais
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 12
                }}>
                <Controller
                  name="employeeCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Matrícula"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="departmentId"
                  control={control}
                  rules={{ required: 'Setor é obrigatório' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.departmentId || !!formErrors.departmentId}>
                      <InputLabel>Setor *</InputLabel>
                      <Select 
                        {...field} 
                        label="Setor *" 
                        disabled={loadingOptions || !selectedCompanyId}
                      >
                        <MenuItem value="">Selecione</MenuItem>
                        {!selectedCompanyId && (
                          <MenuItem value="" disabled>
                            Selecione uma empresa primeiro
                          </MenuItem>
                        )}
                        {filteredDepartments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {(errors.departmentId || formErrors.departmentId) && (
                        <Typography variant="caption" color="error">
                          {errors.departmentId?.message || formErrors.departmentId}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="occupationId"
                  control={control}
                  rules={{ required: 'Cargo é obrigatório' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.occupationId || !!formErrors.occupationId}>
                      <InputLabel>Cargo *</InputLabel>
                      <Select 
                        {...field} 
                        label="Cargo *" 
                        disabled={loadingOptions || !selectedDepartmentId}
                      >
                        <MenuItem value="">Selecione</MenuItem>
                        {!selectedDepartmentId && (
                          <MenuItem value="" disabled>
                            Selecione um setor primeiro
                          </MenuItem>
                        )}
                        {filteredOccupations.map((occupation) => (
                          <MenuItem key={occupation.id} value={occupation.id}>
                            {occupation.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {(errors.occupationId || formErrors.occupationId) && (
                        <Typography variant="caption" color="error">
                          {errors.occupationId?.message || formErrors.occupationId}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="admissionDate"
                  control={control}
                  rules={{ required: 'Data de admissão é obrigatória' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data de Admissão *"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.admissionDate || !!formErrors.admissionDate}
                      helperText={errors.admissionDate?.message || formErrors.admissionDate}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Situação"
                      fullWidth
                      disabled={demitidoGravado}
                      error={!!formErrors.status}
                      helperText={
                        formErrors.status ||
                        (demitidoGravado
                          ? 'Funcionário demitido: para trazê-lo de volta, use Reativar'
                          : 'Demitido exige data de demissão')
                      }
                    >
                      {Object.values(EmployeeStatus).map((situacao) => (
                        <MenuItem key={situacao} value={situacao}>{EMPLOYEE_STATUS_LABELS[situacao]}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="dismissalDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Data de Demissão"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      error={!!formErrors.dismissalDate}
                      helperText={formErrors.dismissalDate}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="leaveDate"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Data de Afastamento" type="date" fullWidth InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="workLocation"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select label="Tipo de Empresa" fullWidth helperText="Onde o funcionário atua (posto de trabalho)">
                      <MenuItem value="">Não informado</MenuItem>
                      {Object.values(WorkLocation).map((w) => (
                        <MenuItem key={w} value={w}>{WORK_LOCATION_LABELS[w]}</MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              {watchWorkLocation === WorkLocation.UNIDADE && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="unitId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Unidade"
                        fullWidth
                        disabled={!selectedCompanyId}
                        helperText={selectedCompanyId ? 'Riscos, exames e fontes da unidade chegam pelo setor do funcionário, não por este campo.' : 'Selecione a empresa primeiro'}
                      >
                        <MenuItem value="">Selecione...</MenuItem>
                        {filteredUnits.map((u) => (
                          <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* Step 4: PCD, CIPA e eSocial */}
          {activeStep === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* Seção PCD */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <AccessibleForward sx={{ color: 'primary.main', fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={600}>Pessoa com Deficiência (PCD)</Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        O funcionário possui deficiência?
                      </Typography>
                      <Controller
                        name="hasDisability"
                        control={control}
                        render={({ field }) => (
                          <FormControl>
                            <RadioGroup {...field} row>
                              <FormControlLabel value="Não" control={<Radio size="small" />} label="Não" />
                              <FormControlLabel value="Sim" control={<Radio size="small" />} label="Sim" />
                            </RadioGroup>
                          </FormControl>
                        )}
                      />
                    </Grid>
                    {watch('hasDisability') === 'Sim' && (
                      <>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <CidAutocomplete
                            value={watch('cidCode')}
                            onChange={(code) => setValue('cidCode', code)}
                            size="small"
                            label="CID"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Controller
                            name="disabilityDescription"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} label="Descrição da Deficiência" fullWidth size="small" placeholder="Detalhes da deficiência" />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Box>

              {/* Seção CIPA */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <HealthAndSafety sx={{ color: 'success.main', fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={600}>CIPA</Typography>
                  <Typography variant="body2" color="text.secondary">Comissão Interna de Prevenção de Acidentes</Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={12}>
                      <Controller
                        name="cipa.isMember"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} size="small" />}
                            label={<Typography variant="body2">Este funcionário é membro da CIPA</Typography>}
                          />
                        )}
                      />
                    </Grid>
                    {watch('cipa.isMember') && (
                      <>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Controller
                            name="cipa.role"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth size="small" error={!!formErrors['cipa.role']}>
                                <InputLabel>Cargo na CIPA *</InputLabel>
                                <Select {...field} label="Cargo na CIPA *">
                                  <MenuItem value="">Selecione</MenuItem>
                                  <MenuItem value="TITULAR">Titular</MenuItem>
                                  <MenuItem value="SUPLENTE">Suplente</MenuItem>
                                </Select>
                                {formErrors['cipa.role'] && (
                                  <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                                    {formErrors['cipa.role']}
                                  </Typography>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Controller
                            name="cipa.startDate"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Data Início *"
                                type="date"
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={!!formErrors['cipa.startDate']}
                                helperText={formErrors['cipa.startDate']}
                              />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Controller
                            name="cipa.endDate"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} label="Data Fim" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Controller
                            name="cipa.term"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} label="Mandato" fullWidth size="small" placeholder="Ex: 2024/2025" />
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <Controller
                            name="esocial.estabilidade"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} label="Data de Estabilidade" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            )}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </Box>

              {/* Seção eSocial */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <AccountBalance sx={{ color: 'warning.main', fontSize: 22 }} />
                  <Typography variant="subtitle1" fontWeight={600}>eSocial</Typography>
                  <Typography variant="body2" color="text.secondary">Condição diferenciada de trabalho</Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="esocial.inicioCondEsocial"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Início da Condição" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="esocial.fimCondEsocial"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Fim da Condição" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="esocial.motivo"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} label="Motivo" fullWidth size="small" />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <AposentadoriaEspecialFields
                    control={control}
                    estado={calculoDoCargo}
                    aviso={aviso}
                    gfip={gfipAtual}
                    enquadramento={enquadramentoAtual}
                    onEditar={registrarEdicaoDaPessoa}
                    onUsarCodigo={usarCodigoCalculado}
                    onManter={() =>
                      setMantidoPara(
                        chaveDoAviso(respostaDoCargo?.codigo ?? null, gfipAtual, enquadramentoAtual)
                      )
                    }
                    onTentarDeNovo={recalcularCargo}
                  />
                </Box>
              </Box>

            </Box>
          )}
        </Paper>

        {/* Navigation Buttons */}
        <Box display="flex" justifyContent="space-between">
          <Button
            type="button"
            size="large"
            sx={BOTAO_DE_NAVEGACAO}
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<NavigateBefore />}
          >
            Voltar
          </Button>

          <Box display="flex" gap={2}>
            <Button
              type="button"
              variant="outlined"
              size="large"
              sx={BOTAO_DE_NAVEGACAO}
              onClick={() => navigate('/employees')}
            >
              Cancelar
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant={aviso.aberta ? 'outlined' : 'contained'}
                size="large"
                sx={BOTAO_DE_NAVEGACAO}
                startIcon={<Save />}
                disabled={saving}
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="contained"
                size="large"
                sx={BOTAO_DE_NAVEGACAO}
                onClick={handleNext}
                endIcon={<NavigateNext />}
              >
                Próximo
              </Button>
            )}
          </Box>
        </Box>
      </form>

      {/* Error Alert */}
      {Object.keys(formErrors).length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Por favor, corrija os erros no formulário antes de continuar.
        </Alert>
      )}
      {/* Company Selection Modal */}
      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleCompanySelect}
      />
    </PageShell>
  );
};
